/**
 * Theme ad landing pages (Sam t5301u/t5317u, Oct 29 2026): home-model page per issue.
 * Hope + relief, inside the struggle. Self-help listening, not therapy; never claim counseling. No "someone who listens".
 * `topics` = real Sharetribe publicData.topics keys (util/sessionTopics.js);
 * only listings that list one of them are shown.
 */
export const TALK_THEMES = {
  divorce: {
    seoTitle: 'Divorce & separation: self-help listening, not therapy',
    seoDescription: 'Going through a divorce or separation? Talk it out with a real peer listener — self-help listening, not therapy. For when you don’t need a therapist, just someone to listen. First 15 min free.',
    topics: ['divorce-separation', 'breakups'],
    kicker: 'Going through a divorce or separation?',
    h1: 'You don’t have to go through divorce alone.',
    chipPrefer: ['divorce', 'separation', 'breakup', 'relationship', 'family'],
    sub: 'The texts you keep rereading, the paperwork you can’t open, the quiet house. Say it out loud when you’re ready, to a real person who’s available now.',
    peopleTitle: 'People who talk through breakups and divorce',
    why: [
      ['chat', 'pink', 'Say what you can’t say to friends', 'No sides, no “I told you so,” no advice you didn’t ask for.'],
      ['people', 'pink', 'Someone outside your circle', 'A real person who isn’t part of the story.'],
      ['shieldcheck', 'blue', 'Completely private', 'Screen names only. Nothing gets back to your ex or your family.'],
      ['cal', 'blue', 'Whenever it hits', 'Talk now, or book a time that fits your day.'],
    ],
  },
  money: {
    seoTitle: 'Money stress: self-help listening, not therapy',
    seoDescription: 'Money stress keeping you up? Say it out loud to a real peer listener — self-help listening, not therapy or financial advice. First 15 min free.',
    topics: ['money-worries'],
    kicker: 'Money stress keeping you up?',
    h1: 'You don’t have to carry money stress alone.',
    chipPrefer: ['money', 'financ', 'stress', 'work'],
    sub: 'Chest tight, calculator open, past-due email blinking. Say it out loud to a real person who’s available now, and breathe a little easier.',
    peopleTitle: 'People who get money stress',
    why: [
      ['chat', 'pink', 'Let the panic out', 'Saying the numbers out loud makes them feel smaller.'],
      ['people', 'pink', 'No judgment, no sales pitch', 'Not a lender, not a coach. A real person to talk to.'],
      ['shieldcheck', 'blue', 'Private', 'Screen names only. Nobody sees your finances.'],
      ['cal', 'blue', 'Only pay for what you use', 'No subscription and no commitment.'],
    ],
  },
  parenting: {
    seoTitle: 'Parenting burnout: self-help listening, not therapy',
    seoDescription: 'Running on empty as a parent? Vent to a real peer listener — self-help listening, not therapy. For when you don’t need a therapist, just someone to hear you. First 15 min free.',
    topics: ['parenting'],
    kicker: 'Parenting burnout? Running on empty?',
    chipPrefer: ['parent', 'family', 'stress'],
    h1: 'You don’t have to carry parenting burnout alone.',
    sub: 'The meltdowns, the guilt, the to-do list that never ends, the moment you finally sit down and feel like you’re failing. Say it out loud to a real person who’s available right now, and feel a little lighter.',
    peopleTitle: 'People who get parenting overwhelm',
    why: [
      ['chat', 'pink', 'Say the part you can’t say at pickup', 'The exhaustion, the resentment, the guilt. Out loud, no judgment.'],
      ['people', 'pink', 'A real person, right now', 'Not a parenting forum, not a bot. One person, by text, voice or video.'],
      ['shieldcheck', 'blue', 'Private', 'Screen names only. Nothing gets back to family or school.'],
      ['cal', 'blue', 'Fits around nap time', 'Talk now, or book a time after bedtime.'],
    ],
  },
  spirituality: {
    seoTitle: 'Faith & meaning: self-help listening, not therapy',
    seoDescription: 'Wrestling with faith, doubt or meaning? Talk it through with a real peer listener — self-help listening, not therapy. No sermon, no judgment. First 15 min free.',
    topics: ['spirituality-meaning'],
    kicker: 'Faith, doubt, meaning, purpose.',
    h1: 'You don’t have to wrestle with faith and meaning alone.',
    chipPrefer: ['spiritual', 'faith', 'meaning', 'religio', 'prayer', 'grief'],
    sub: 'The faith that feels shaken, the prayer that went quiet, the search for meaning after a loss or a change you didn’t see coming. Say it out loud to a real person who’s available now, and feel a little steadier.',
    peopleTitle: 'People who talk through faith and meaning',
    why: [
      ['chat', 'pink', 'Ask the questions out loud', 'Doubt, hope, anger, wonder. No sermon, no right answer required.'],
      ['people', 'pink', 'Someone outside your circle', 'Not your congregation, not your family. A real person who won’t judge.'],
      ['shieldcheck', 'blue', 'Completely private', 'Screen names only. Nothing gets back to your church or community.'],
      ['cal', 'blue', 'Whenever the questions come', 'Talk now, or book a time that fits your day.'],
    ],
  },
  'life-changes': {
    seoTitle: 'Big life changes: self-help listening, not therapy',
    seoDescription: 'Facing a move, job loss or big change? Think out loud with a real peer listener — self-help listening, not therapy. For when you don’t need a therapist. First 15 min free.',
    topics: ['major-life-changes'],
    kicker: 'Going through a big life change?',
    h1: 'You don’t have to face a big life change alone.',
    chipPrefer: ['life change', 'change', 'moving', 'work', 'family'],
    sub: 'The move, the job that ended, the empty nest, the life that suddenly looks different. Say it out loud to a real person who’s available now, and find your footing.',
    peopleTitle: 'People who talk through major life changes',
    why: [
      ['chat', 'pink', 'Think out loud', 'Untangle what happened and what comes next, at your own pace.'],
      ['people', 'pink', 'Someone outside the change', 'A real person with no stake in your decision. No advice you didn’t ask for.'],
      ['shieldcheck', 'blue', 'Private', 'Screen names only. Nothing gets back to work or family.'],
      ['cal', 'blue', 'Fits the upheaval', 'Talk now, or book a time between the boxes.'],
    ],
  },
  work: {
    seoTitle: 'Work & career stress: self-help listening, not therapy',
    seoDescription: 'Work stress following you home? Vent to a real peer listener — self-help listening, not therapy or career coaching. For when you just need someone to hear you. First 15 min free.',
    topics: ['work-career', 'career-advice', 'stress-venting'],
    kicker: 'Work stress following you home?',
    h1: 'You don’t have to carry work stress alone.',
    chipPrefer: ['work', 'career', 'job', 'stress', 'boss'],
    sub: 'The Sunday dread, the boss who never lets up, the inbox that never empties. Say it out loud to a real person who’s available now, and leave a little of it behind.',
    peopleTitle: 'People who get work stress',
    why: [
      ['chat', 'pink', 'Vent without consequences', 'Say what you can’t say in the meeting. Nobody from work is listening.'],
      ['people', 'pink', 'Someone outside the office', 'A real person with no stake in your job, your team or your review.'],
      ['shieldcheck', 'blue', 'Completely private', 'Screen names only. Nothing gets back to your manager or HR.'],
      ['cal', 'blue', 'Before or after the shift', 'Talk now, or book a time on your lunch break.'],
    ],
  },
  loneliness: {
    seoTitle: 'Feeling lonely: self-help listening, not therapy',
    seoDescription: 'Feeling lonely or unheard? Talk with a real peer listener who’s available now — self-help listening, not therapy. Just a real conversation. First 15 min free.',
    topics: ['loneliness', 'friendship', 'new-in-town', 'everyday-conversation'],
    kicker: 'Feeling lonely lately?',
    h1: 'You don’t have to feel alone tonight.',
    chipPrefer: ['lonel', 'friend', 'conversation', 'new in town', 'chat'],
    sub: 'The quiet apartment, the phone that doesn’t ring, the day you didn’t say a word out loud. Talk to a real person who’s available now and actually wants to hear how you are.',
    peopleTitle: 'People who are happy to talk',
    why: [
      ['chat', 'pink', 'A real conversation', 'About your day, your week, or nothing in particular.'],
      ['people', 'pink', 'A real person, right now', 'Not a feed, not a bot. One person, by text, voice or video.'],
      ['shieldcheck', 'blue', 'Private', 'Screen names only. Share as much or as little as you like.'],
      ['cal', 'blue', 'Whenever it’s quiet', 'Talk now, or book a regular time to look forward to.'],
    ],
  },
  breakup: {
    seoTitle: 'Breakup support: self-help listening, not therapy',
    seoDescription: 'Going through a breakup? Talk it out with a real peer listener — self-help listening, not therapy. No sides, no “just move on.” First 15 min free.',
    topics: ['breakups', 'relationships-dating', 'divorce-separation'],
    kicker: 'Going through a breakup?',
    h1: 'You don’t have to get through a breakup alone.',
    chipPrefer: ['breakup', 'relationship', 'heartbreak', 'dating', 'divorce'],
    sub: 'The playlist you can’t skip, the photos you can’t delete, the urge to text them at 2am. Say it out loud to a real person who’s available now instead.',
    peopleTitle: 'People who talk through breakups',
    why: [
      ['chat', 'pink', 'Say it all, again if you need to', 'No eye-rolls, no “you’re better off.” Just someone listening.'],
      ['people', 'pink', 'Someone outside your circle', 'Not your mutual friends. A real person who isn’t part of the story.'],
      ['shieldcheck', 'blue', 'Completely private', 'Screen names only. Nothing gets back to your ex.'],
      ['cal', 'blue', 'When the late nights hit', 'Talk now, or book a time that fits your day.'],
    ],
  },
  dating: {
    seoTitle: 'Dating stress: self-help listening, not therapy',
    seoDescription: 'Tired of the apps, ghosting or dating anxiety? Talk it through with a real peer listener — self-help listening, not therapy, and not a dating service. First 15 min free.',
    topics: ['dating', 'relationships-dating', 'confidence-self-esteem'],
    kicker: 'Worn out by dating?',
    h1: 'You don’t have to sort out dating stress alone.',
    chipPrefer: ['dating', 'relationship', 'confidence', 'breakup'],
    sub: 'The ghosting, the first-date nerves, the swiping that feels like a second job. Talk it through with a real person who’s available now — a listener, not a match.',
    peopleTitle: 'People who talk through dating and relationships',
    why: [
      ['chat', 'pink', 'Talk it through, out loud', 'Replay the date, the text, the silence. Hear yourself think.'],
      ['people', 'pink', 'A listener, not a match', 'This isn’t a dating app. Just a real person who’ll hear you out.'],
      ['shieldcheck', 'blue', 'Completely private', 'Screen names only. Nothing gets back to anyone you’re seeing.'],
      ['cal', 'blue', 'Before or after the date', 'Talk now, or book a time that fits your week.'],
    ],
  },
  anxiety: {
    seoTitle: 'Overthinking & worry: self-help listening, not therapy',
    seoDescription: 'Can’t stop overthinking? Say it out loud to a real peer listener — self-help listening, not therapy or treatment. Listeners aren’t counselors. First 15 min free.',
    topics: ['stress-venting', 'confidence-self-esteem', 'everyday-conversation'],
    kicker: 'Stuck in your head?',
    h1: 'You don’t have to overthink it alone.',
    chipPrefer: ['stress', 'worry', 'overthink', 'vent', 'confidence'],
    sub: 'The thoughts on repeat, the what-ifs at midnight, the conversation you keep replaying. Say it out loud to a real person who’s available now and get it out of your head. Listeners are peers, not counselors — in a crisis, call or text 988.',
    peopleTitle: 'People who get overthinking',
    why: [
      ['chat', 'pink', 'Get it out of your head', 'Saying it out loud can make the loop feel smaller.'],
      ['people', 'pink', 'A real person, not a counselor', 'A peer listener who’ll hear you out. Self-help listening, not treatment.'],
      ['shieldcheck', 'blue', 'Completely private', 'Screen names only. Share as much or as little as you like.'],
      ['cal', 'blue', 'When the thoughts won’t stop', 'Talk now, or book a time that fits your day.'],
    ],
  },
  family: {
    seoTitle: 'Family stress: self-help listening, not therapy',
    seoDescription: 'Family tension wearing you down? Vent to a real peer listener — self-help listening, not therapy or family counseling. No sides, no judgment. First 15 min free.',
    topics: ['family', 'caregiving', 'parenting'],
    kicker: 'Family stress wearing you down?',
    h1: 'You don’t have to carry family stress alone.',
    chipPrefer: ['family', 'parent', 'sibling', 'caregiv', 'holiday'],
    sub: 'The group chat you muted, the holiday you’re dreading, the same argument for the tenth year. Say it out loud to a real person who’s available now, and feel a little lighter.',
    peopleTitle: 'People who get family stress',
    why: [
      ['chat', 'pink', 'Say what you can’t say at the table', 'No sides, no guilt trips. Just someone listening.'],
      ['people', 'pink', 'Someone outside the family', 'A real person who isn’t related to anyone in the story.'],
      ['shieldcheck', 'blue', 'Completely private', 'Screen names only. Nothing gets back to your family.'],
      ['cal', 'blue', 'Whenever it boils over', 'Talk now, or book a time that fits your day.'],
    ],
  },
  'empty-nest': {
    seoTitle: 'Empty nest: self-help listening, not therapy',
    seoDescription: 'Kids moved out and the house feels too quiet? Talk with a real peer listener — self-help listening, not therapy. First 15 min free.',
    topics: ['major-life-changes', 'parenting', 'family', 'loneliness'],
    kicker: 'House feels too quiet?',
    h1: 'You don’t have to face the empty nest alone.',
    chipPrefer: ['empty nest', 'parent', 'family', 'change', 'lonel'],
    sub: 'The bedroom that stays tidy now, the dinner for two, the question of who you are when nobody needs a ride. Say it out loud to a real person who’s available now, and find what’s next.',
    peopleTitle: 'People who get the empty-nest years',
    why: [
      ['chat', 'pink', 'Name the mixed feelings', 'Proud and sad and relieved at once. All of it is welcome.'],
      ['people', 'pink', 'Someone who gets it', 'A real person outside the family, no advice you didn’t ask for.'],
      ['shieldcheck', 'blue', 'Private', 'Screen names only. Nothing gets back to the kids.'],
      ['cal', 'blue', 'Whenever it’s quiet', 'Talk now, or book a time that fits your new routine.'],
    ],
  },
  'new-parent': {
    seoTitle: 'New parent overwhelm: self-help listening, not therapy',
    seoDescription: 'New baby, no sleep, big feelings? Vent to a real peer listener — self-help listening, not therapy or medical advice. First 15 min free.',
    topics: ['parenting', 'major-life-changes', 'family'],
    kicker: 'New parent and running on empty?',
    h1: 'You don’t have to do the newborn days alone.',
    chipPrefer: ['parent', 'baby', 'new parent', 'family', 'stress'],
    sub: 'The 3am feeds, the advice from everyone, the feeling that you’re supposed to be enjoying every second. Say it out loud to a real person who’s available now, and feel a little less alone.',
    peopleTitle: 'People who get new-parent overwhelm',
    why: [
      ['chat', 'pink', 'Say the part nobody posts', 'The exhaustion, the doubt, the I-miss-my-old-life. No judgment.'],
      ['people', 'pink', 'A real person, right now', 'Not a forum, not a bot. One person, by text, voice or video.'],
      ['shieldcheck', 'blue', 'Private', 'Screen names only. Nothing gets back to family.'],
      ['cal', 'blue', 'Fits around feeds and naps', 'Talk now with one hand free, or book a time.'],
    ],
  },
  'grief': {
    seoTitle: "Grief & loss: self-help listening, not therapy",
    seoDescription: "Grieving a loss? Talk about them with a real peer listener — self-help listening, not therapy or grief counseling. No timeline, no judgment. First 15 min free.",
    topics: ["grief-loss", "spirituality-meaning"],
    kicker: "Grieving someone or something?",
    h1: "You don’t have to carry grief alone.",
    chipPrefer: ["grief", "loss", "bereave", "faith"],
    sub: "The empty chair, the voicemail you keep, the days that hit out of nowhere. Talk about them out loud to a real person who’s available now, at your own pace.",
    peopleTitle: "People who talk through grief and loss",
    why: [
      ["chat", "pink", "Talk about them", "Share the memories, the anger, the guilt. Nothing is too much."],
      ["people", "pink", "Someone who’ll just listen", "No “they’re in a better place.” A real person who lets you grieve."],
      ["shieldcheck", "blue", "Completely private", "Screen names only. Share as much or as little as you like."],
      ["cal", "blue", "When the waves hit", "Talk now, or book a time that fits your day."],
    ],
  },
  'friendship': {
    seoTitle: "Friendship struggles: self-help listening, not therapy",
    seoDescription: "Friendship falling apart or drifting? Talk it through with a real peer listener — self-help listening, not therapy. No sides. First 15 min free.",
    topics: ["friendship", "loneliness", "everyday-conversation"],
    kicker: "Friendship drama or drifting apart?",
    h1: "You don’t have to figure out friendships alone.",
    chipPrefer: ["friend", "lonel", "conversation"],
    sub: "The group chat that went quiet, the friend who changed, the falling-out you keep replaying. Talk it through with a real person who’s available now.",
    peopleTitle: "People who talk through friendships",
    why: [
      ["chat", "pink", "Talk it through", "Replay what happened and hear yourself think."],
      ["people", "pink", "Someone outside the group", "A real person who isn’t friends with anyone in the story."],
      ["shieldcheck", "blue", "Completely private", "Screen names only. Nothing gets back to your friends."],
      ["cal", "blue", "Whenever you need it", "Talk now, or book a time that fits your day."],
    ],
  },
  'burnout': {
    seoTitle: "Burnout: self-help listening, not therapy",
    seoDescription: "Burned out and running on empty? Vent to a real peer listener — self-help listening, not therapy. Say it out loud and breathe. First 15 min free.",
    topics: ["stress-venting", "work-career", "caregiving"],
    kicker: "Running on fumes?",
    h1: "You don’t have to push through burnout alone.",
    chipPrefer: ["burnout", "stress", "work", "tired", "vent"],
    sub: "Too tired to rest, too wired to sleep, too much on the list. Say it out loud to a real person who’s available now, and set some of it down.",
    peopleTitle: "People who get burnout",
    why: [
      ["chat", "pink", "Let it out", "Name everything that’s draining you, without fixing it yet."],
      ["people", "pink", "A real person, right now", "Not a productivity hack. One person, by text, voice or video."],
      ["shieldcheck", "blue", "Private", "Screen names only. Nothing gets back to work or family."],
      ["cal", "blue", "Fits a full schedule", "Talk now, or book ten minutes for yourself."],
    ],
  },
  'postpartum': {
    seoTitle: "Postpartum peer support: self-help listening, not therapy",
    seoDescription: "Postpartum and overwhelmed? Talk with a real peer listener — peer support and self-help listening, not therapy or medical advice. First 15 min free.",
    topics: ["parenting", "major-life-changes", "family"],
    kicker: "New baby and feeling unlike yourself?",
    h1: "You don’t have to go through the postpartum months alone.",
    chipPrefer: ["parent", "baby", "mom", "family", "stress"],
    sub: "The sleepless nights, the body that feels new, the feelings nobody warned you about. Say it out loud to a real person who’s available now. Listeners are peers, not medical providers — for health concerns, talk to your doctor; in a crisis, call or text 988.",
    peopleTitle: "People who get the postpartum months",
    why: [
      ["chat", "pink", "Say the part nobody posts", "The exhaustion, the doubt, the mixed feelings. No judgment."],
      ["people", "pink", "A peer, not a provider", "A real person who listens. Not medical or clinical care."],
      ["shieldcheck", "blue", "Private", "Screen names only. Nothing gets back to family."],
      ["cal", "blue", "Fits around feeds", "Talk now with one hand free, or book a time."],
    ],
  },
  'caregiving': {
    seoTitle: "Caregiver stress: self-help listening, not therapy",
    seoDescription: "Caring for a parent, partner or child? Vent to a real peer listener — self-help listening, not therapy. A few minutes just for you. First 15 min free.",
    topics: ["caregiving", "family", "stress-venting"],
    kicker: "Caring for someone you love?",
    h1: "You don’t have to carry caregiving alone.",
    chipPrefer: ["caregiv", "family", "parent", "stress"],
    sub: "The appointments, the medications, the guilt when you need a break. Say it out loud to a real person who’s available now, and remember you matter too.",
    peopleTitle: "People who get caregiver stress",
    why: [
      ["chat", "pink", "A few minutes for you", "Talk about you for once, not the care schedule."],
      ["people", "pink", "Someone who gets it", "A real person outside the family, no advice you didn’t ask for."],
      ["shieldcheck", "blue", "Private", "Screen names only. Nothing gets back to family."],
      ["cal", "blue", "Between the shifts", "Talk now, or book a time that fits around care."],
    ],
  },
  'adhd': {
    seoTitle: "ADHD peer talk: self-help listening, not therapy",
    seoDescription: "Living with ADHD overwhelm? Talk it out with a real peer listener — peer talk and self-help listening, not therapy, diagnosis or treatment. First 15 min free.",
    topics: ["stress-venting", "confidence-self-esteem", "everyday-conversation"],
    kicker: "Brain going a hundred directions?",
    h1: "You don’t have to sort out the ADHD overwhelm alone.",
    chipPrefer: ["adhd", "focus", "stress", "confidence", "vent"],
    sub: "The open tabs, the forgotten texts, the shame spiral after another missed thing. Talk it out with a real person who’s available now. Peer talk only — listeners don’t diagnose, treat or give medical advice.",
    peopleTitle: "People who get the ADHD brain",
    why: [
      ["chat", "pink", "Think out loud", "Untangle the to-do pile by talking it through."],
      ["people", "pink", "A peer, not a clinician", "A real person who listens. No diagnosis, no treatment."],
      ["shieldcheck", "blue", "Private", "Screen names only. Share what you like."],
      ["cal", "blue", "Short and flexible", "Talk now for a few minutes, or book a time."],
    ],
  },
  'anger': {
    seoTitle: "Anger & frustration: self-help listening, not therapy",
    seoDescription: "Need to vent anger or frustration? Say it out loud to a real peer listener — self-help listening, not therapy or anger management. First 15 min free.",
    topics: ["stress-venting", "family", "work-career"],
    kicker: "So frustrated you could scream?",
    h1: "You don’t have to sit on that anger alone.",
    chipPrefer: ["anger", "vent", "stress", "frustrat", "work", "family"],
    sub: "The thing they said, the unfair call, the frustration with nowhere to go. Vent it out loud to a real person who’s available now, before you send that text.",
    peopleTitle: "People who are good at hearing a vent",
    why: [
      ["chat", "pink", "Vent safely", "Say it all here instead of to the person who set you off."],
      ["people", "pink", "No judgment", "A real person who lets you be mad for a minute."],
      ["shieldcheck", "blue", "Completely private", "Screen names only. Nothing gets back to anyone."],
      ["cal", "blue", "Right when it hits", "Talk now, or book a time to cool down."],
    ],
  },
  'sleep': {
    seoTitle: "Can’t sleep, racing mind: self-help listening, not therapy",
    seoDescription: "Mind racing when you should be asleep? Talk it out with a real peer listener — self-help listening, not therapy or medical advice. First 15 min free.",
    topics: ["stress-venting", "everyday-conversation", "loneliness"],
    kicker: "Awake again at 2am?",
    h1: "You don’t have to lie awake with it alone.",
    chipPrefer: ["sleep", "night", "stress", "worry", "conversation"],
    sub: "The ceiling, the replayed conversation, the list that won’t stop. Say it out loud to a real person who’s available now, and let your mind slow down. Listeners aren’t sleep or medical specialists.",
    peopleTitle: "People who are up late too",
    why: [
      ["chat", "pink", "Empty your head", "Say the loop out loud so it stops circling."],
      ["people", "pink", "A real person, late", "Many listeners are available evenings and nights."],
      ["shieldcheck", "blue", "Private", "Screen names only. Text if you don’t want to wake anyone."],
      ["cal", "blue", "Whenever you’re up", "Talk now, or book a wind-down time."],
    ],
  },
  'confidence': {
    seoTitle: "Confidence & self-doubt: self-help listening, not therapy",
    seoDescription: "Struggling with self-doubt? Talk it through with a real peer listener — self-help listening, not therapy or coaching. First 15 min free.",
    topics: ["confidence-self-esteem", "work-career", "dating"],
    kicker: "Second-guessing yourself?",
    h1: "You don’t have to fight self-doubt alone.",
    chipPrefer: ["confidence", "self", "esteem", "work", "dating"],
    sub: "The interview tomorrow, the voice that says you’re not enough, the thing you want to try but keep putting off. Talk it through with a real person who’s available now.",
    peopleTitle: "People who help you hear yourself",
    why: [
      ["chat", "pink", "Say it out loud", "Hearing yourself say it can quiet the inner critic."],
      ["people", "pink", "Someone in your corner", "A real person who listens without judging."],
      ["shieldcheck", "blue", "Private", "Screen names only."],
      ["cal", "blue", "Before the big moment", "Talk now, or book a time before the interview or date."],
    ],
  },
  'moving': {
    seoTitle: "Moving & new in town: self-help listening, not therapy",
    seoDescription: "Just moved or new in town? Talk with a real peer listener — self-help listening, not therapy. Someone to talk to while you settle in. First 15 min free.",
    topics: ["new-in-town", "major-life-changes", "loneliness"],
    kicker: "New city, no one to talk to?",
    h1: "You don’t have to settle in alone.",
    chipPrefer: ["new in town", "moving", "reloc", "lonel", "change"],
    sub: "The boxes still taped, the streets you don’t know, the friends who are a time zone away. Talk to a real person who’s available now while you find your footing.",
    peopleTitle: "People who get being new in town",
    why: [
      ["chat", "pink", "Someone to talk to", "About the move, the new place, or anything at all."],
      ["people", "pink", "A real person, right now", "Not a feed, not a bot. One person, by text, voice or video."],
      ["shieldcheck", "blue", "Private", "Screen names only."],
      ["cal", "blue", "Between the boxes", "Talk now, or book a time that fits your day."],
    ],
  },
  'veterans': {
    seoTitle: "Veterans peer talk: self-help listening, not therapy",
    seoDescription: "Veteran adjusting to civilian life? Talk it through with a real peer listener — self-help listening, not therapy or VA services. First 15 min free.",
    topics: ["major-life-changes", "work-career", "stress-venting"],
    kicker: "Back home, but not quite settled?",
    h1: "You don’t have to carry it alone.",
    chipPrefer: ["veteran", "military", "work", "change", "stress"],
    sub: "The transition, the job search, the people who don’t quite get it. Talk to a real person who’s available now. Peer listening only — not therapy or VA services. Veterans in crisis: dial 988, then press 1.",
    peopleTitle: "People who listen to veterans",
    why: [
      ["chat", "pink", "Say it straight", "No small talk required. Say what’s on your mind."],
      ["people", "pink", "A real person", "A peer listener, not a counselor or clinician."],
      ["shieldcheck", "blue", "Completely private", "Screen names only."],
      ["cal", "blue", "On your schedule", "Talk now, or book a time that fits your day."],
    ],
  },
  'lgbtq': {
    seoTitle: "LGBTQ+ peer talk: self-help listening, not therapy",
    seoDescription: "LGBTQ+ and need someone to talk to? Talk with a real peer listener — self-help listening, not therapy. Judgment-free. First 15 min free.",
    topics: ["identity-belonging", "family", "relationships-dating"],
    kicker: "Need someone who gets it?",
    h1: "You don’t have to figure it out alone.",
    chipPrefer: ["lgbt", "queer", "identity", "belong", "family"],
    sub: "Coming out, family tension, dating, or just being yourself in a place that doesn’t always make it easy. Talk to a real person who’s available now.",
    peopleTitle: "People who listen without judgment",
    why: [
      ["chat", "pink", "Be yourself", "Talk about identity, family, dating or anything at all."],
      ["people", "pink", "No judgment", "A real person who listens and respects you."],
      ["shieldcheck", "blue", "Completely private", "Screen names only. Nothing gets back to anyone."],
      ["cal", "blue", "Whenever you need it", "Talk now, or book a time that fits your day."],
    ],
  },
  'chronic-illness': {
    seoTitle: "Living with chronic illness: self-help listening, not therapy",
    seoDescription: "Living with chronic illness? Vent to a real peer listener — self-help listening, not therapy or medical advice. Talk from the couch. First 15 min free.",
    topics: ["stress-venting", "major-life-changes", "loneliness"],
    kicker: "Tired of explaining how you feel?",
    h1: "You don’t have to carry chronic illness alone.",
    chipPrefer: ["chronic", "illness", "health", "stress", "lonel"],
    sub: "The canceled plans, the appointments, the people who say you look fine. Say it out loud to a real person who’s available now. Listeners are peers, not medical providers — no medical advice.",
    peopleTitle: "People who listen to the hard days",
    why: [
      ["chat", "pink", "Say how it really is", "No need to look fine here."],
      ["people", "pink", "A peer, not a provider", "A real person who listens. No medical advice."],
      ["shieldcheck", "blue", "Private", "Screen names only."],
      ["cal", "blue", "On low-energy days", "Talk by text from bed, or book a time."],
    ],
  },
  'retirement': {
    seoTitle: "Retirement transition: self-help listening, not therapy",
    seoDescription: "Adjusting to retirement? Talk it through with a real peer listener — self-help listening, not therapy or financial advice. First 15 min free.",
    topics: ["major-life-changes", "everyday-conversation", "loneliness"],
    kicker: "Retired and wondering what’s next?",
    h1: "You don’t have to figure out retirement alone.",
    chipPrefer: ["retire", "change", "conversation", "lonel", "hobb"],
    sub: "The calendar that suddenly opened up, the colleagues you miss, the question of who you are without the job. Talk to a real person who’s available now.",
    peopleTitle: "People who enjoy a good conversation",
    why: [
      ["chat", "pink", "Think out loud", "About what you miss and what comes next."],
      ["people", "pink", "A real conversation", "A person who’s glad to hear your stories."],
      ["shieldcheck", "blue", "Private", "Screen names only."],
      ["cal", "blue", "Any time of day", "Talk now, or book a regular time."],
    ],
  },
};

export const TALK_THEME_IDS = Object.keys(TALK_THEMES);
export const talkThemePath = id => `/talk/${id}`;
